import {
  isRoundV3,
  type RoundSnapshot,
  type RoundSnapshotV3,
} from "../round/round-types.js";
import { selectHoleEntry } from "../round/round-selectors.js";
import type { ContestSnapshot } from "./contest-types.js";

/** Contest records can multiply; their physical round and hole facts do not. */
export function selectContests(snapshot: RoundSnapshot): ContestSnapshot[] {
  return isRoundV3(snapshot)
    ? [snapshot.mainContest, ...(snapshot.remainderContests ?? [])]
    : [];
}

/** Only physical strokes that contributed to a decided match need correction. */
export function selectStrokeScoreLocked(
  snapshot: RoundSnapshot,
  holeNumber: number,
): boolean {
  return selectContests(snapshot).some((contest) => {
    const index = contest.holeNumbers.indexOf(holeNumber);
    return Boolean(
      contest.format === "match-play" &&
      contest.result &&
      index >= 0 &&
      index < contest.result.holesPlayed &&
      !contest.holeConcessions?.[holeNumber],
    );
  });
}

export function selectContest(
  snapshot: RoundSnapshotV3,
  contestId = snapshot.mainContest.id,
): ContestSnapshot {
  const contest = selectContests(snapshot).find(
    (item) => item.id === contestId,
  );
  if (!contest) throw new Error("The contest does not belong to this round.");
  return contest;
}

export function selectNextContestHole(
  snapshot: RoundSnapshotV3,
  contestId?: string,
): number | null {
  const contest = selectContest(snapshot, contestId);
  if (contest.result) return null;
  return (
    contest.holeNumbers.find(
      (number) =>
        !contest.holeConcessions?.[number] &&
        contest.sides.some((side) =>
          side.participantIds.some(
            (id) => selectHoleEntry(snapshot, id, number).status === "unset",
          ),
        ),
    ) ?? null
  );
}

/** Only a still-unplayed suffix may become the next separately agreed point. */
export function selectAvailableRemainderHoles(
  snapshot: RoundSnapshotV3,
  parentContestId?: string,
): number[] {
  const contests = selectContests(snapshot);
  const parent = parentContestId
    ? contests.find((item) => item.id === parentContestId)
    : contests.at(-1);
  if (
    !parent ||
    !parent.result ||
    parent.format !== "match-play" ||
    parent.qualification === "needs-review"
  )
    return [];
  const remaining = parent.holeNumbers.slice(parent.result.holesPlayed);
  let lastRecorded = -1;
  for (let index = 0; index < remaining.length; index += 1) {
    if (
      snapshot.participants.some(
        (participant) =>
          selectHoleEntry(snapshot, participant.id, remaining[index]!)
            .status !== "unset",
      )
    )
      lastRecorded = index;
  }
  return remaining.slice(lastRecorded + 1);
}

/** A historical import can attest a prior agreement after its scores are read. */
export function selectImportedRemainderHoles(
  snapshot: RoundSnapshotV3,
  parentContestId?: string,
): number[] {
  if (snapshot.rules.kind !== "ggb-import" || snapshot.importedTotals)
    return [];
  const parent = selectContests(snapshot).at(-1);
  if (
    !parent ||
    (parentContestId && parent.id !== parentContestId) ||
    parent.format !== "match-play" ||
    parent.qualification === "needs-review" ||
    parent.result?.decisionReason !== "early-win"
  )
    return [];
  const remaining = parent.holeNumbers.slice(parent.result.holesPlayed);
  return remaining.every((hole) =>
    snapshot.participants.every((player) => {
      const entry = selectHoleEntry(snapshot, player.id, hole);
      return entry.status === "scored" && entry.strokes !== null;
    }),
  )
    ? remaining
    : [];
}
